import { useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { HumanReviewRequestsService } from 'src/services/human-review-requests.service'
import type {
  EHumanReviewRequestProblem,
  EHumanReviewResolution,
  EHumanReviewTab,
} from './human-review-requests.type'

const PAGE_SIZE = 50

type THumanReviewListFilters = {
  subject: EHumanReviewTab
  problemsFilters: EHumanReviewRequestProblem[]
  resolutionsFilters: EHumanReviewResolution[]
  medEventIdsFilters: number[]
}

const useHumanReviewList = (filters: THumanReviewListFilters) => {
  const { project } = useCurrentProject()

  const { data, error, isFetchNextPageError, ...query } = useInfiniteQuery({
    queryKey: [
      'human-review-requests-list',
      project.id,
      filters.subject,
      filters.problemsFilters,
      filters.resolutionsFilters,
      filters.medEventIdsFilters,
    ],
    queryFn: ({ pageParam }) =>
      HumanReviewRequestsService.getList(project.id, { ...filters, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  })

  const items = useMemo(() => data?.pages.flatMap((page) => page.items), [data])

  return {
    items,
    // A failing next page must not replace the pages already loaded: the button stays there to retry
    error: isFetchNextPageError ? null : error,
    isLoading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}

export default useHumanReviewList
